export const SHARE_SOURCE_KEYS = [
  "wechat", "douyin", "discord", "dingtalk", "wecom", "feishu", "weibo", "qq", "qzone",
  "facebook", "x", "whatsapp", "telegram", "line", "linkedin", "reddit", "pinterest",
  "tumblr", "vk", "bluesky", "system", "copy_link", "copy_text", "email", "print",
] as const;
export type ShareSource = typeof SHARE_SOURCE_KEYS[number];
export type ShareAttribution = { shareId: string; source: ShareSource; videoId: string; landingId: string };
const ID_PATTERN = /^[a-zA-Z0-9_-]{16,80}$/;
const STORAGE_KEY = "neptuneShareLandingV1";
export const ATTRIBUTION_WINDOW_MS = 30 * 60 * 1000;
export const newShareId = () => globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;

export function makeVideoShareUrl(videoId: string, href?: string, source?: ShareSource, shareId?: string) {
  const url = new URL(href || "https://seaking.vip/tiktok/");
  url.hash = "";
  url.search = ""; // Do not forward authentication or the previous sharer's attribution.
  if (!url.pathname.endsWith("/")) url.pathname += "/";
  url.searchParams.set("video", videoId);
  if (source && shareId && ID_PATTERN.test(shareId) && SHARE_SOURCE_KEYS.includes(source)) {
    url.searchParams.set("share_source", source);
    url.searchParams.set("share_id", shareId);
  }
  return url.toString();
}

export function captureShareAttribution(href: string, storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">, now = Date.now()) {
  const url = new URL(href);
  const shareId = url.searchParams.get("share_id") || "";
  const source = url.searchParams.get("share_source") as ShareSource;
  const videoId = (url.searchParams.get("video") || "").slice(0, 160);
  if (!ID_PATTERN.test(shareId) || !SHARE_SOURCE_KEYS.includes(source) || !videoId) {
    try { storage?.removeItem(STORAGE_KEY); } catch {}
    return null; // A new direct entry never inherits an old shared visit.
  }
  let previous: any = null;
  try { previous = JSON.parse(storage?.getItem(STORAGE_KEY) || "null"); } catch {}
  if (previous?.shareId === shareId && previous?.source === source && previous?.videoId === videoId &&
      ID_PATTERN.test(previous?.landingId || "") && Number(previous.expiresAt) > now &&
      Number(previous.expiresAt) <= now + ATTRIBUTION_WINDOW_MS) return previous as ShareAttribution & { expiresAt: number };
  const attribution = { shareId, source, videoId, landingId: newShareId(), expiresAt: now + ATTRIBUTION_WINDOW_MS };
  try { storage?.setItem(STORAGE_KEY, JSON.stringify(attribution)); } catch {}
  return attribution;
}

// Capture before the app replaces ?video= during navigation. Keep the original
// landing's video, not the next video the recipient happens to watch.
const initialAttribution = (() => {
  if (typeof window === "undefined" || !window.location?.href) return null;
  let storage: Storage | undefined;
  try { storage = window.sessionStorage; } catch {}
  return captureShareAttribution(window.location.href, storage);
})();
export function getShareAttribution(): ShareAttribution | undefined {
  // The 30-minute window deduplicates reloads. Do not lose attribution midway
  // through a long video just because its playback lasts more than 30 minutes.
  if (!initialAttribution) return undefined;
  const { expiresAt, ...attribution } = initialAttribution;
  return attribution;
}
